import { Component, EventEmitter, Input, Output, computed, effect, inject, signal } from '@angular/core';
import { CustomizationGroup } from 'src/app/interfaces/customization-group';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { Navigation2Service } from '../../services/navigation2.service';
import { AttributeMenuService } from '../../services/attribute-menu.service';
import { MatDialog } from '@angular/material/dialog';
import { CheckoutDialogComponent } from '../checkout.dialog/checkout.dialog.component';
import { assert } from 'src/app/utils/assert.util';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { CheckoutService } from '../../services/checkout.service';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { CheckoutSummaryComponent } from '../checkout-summary/checkout-summary.component';

@Component({
  selector: 'app-attribute-menu',
  templateUrl: './attribute-menu.component.html',
  styleUrls: ['./attribute-menu.component.scss']
})
export class AttributeMenuComponent {
  private mainImageService = inject(MainImageService);
  private loadingService = inject(LoadingService);
  private rulesService = inject(RulesService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private dialog = inject(MatDialog);
  private selectedAttributeService = inject(SelectedAttributeService);
  private navigationService = inject(Navigation2Service);
  private attributeMenuService = inject(AttributeMenuService);
  private checkoutService = inject(CheckoutService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneSizesService = inject(StoneSizesService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneArrangementsService = inject(StoneArrangementsService);
  private stoneShapesService = inject(StoneShapesService);
  private stoneTypesService = inject(StoneTypesService);
  private stoneSettingsService = inject(StoneSettingsService);
  private stoneCountsProvider = inject(StoneCountsProviderService);

  @Input() shouldAnimateOut: boolean = false;
  @Input() shouldAnimateIn: boolean = false;
  selectedAttributes = computed(() => this.selectedAttributeService.getValue());
  activeItem = computed(() => this.navigationService.activeView().title);
  isRetailerOnly = computed(() => this.checkoutService.isRetailerOnly());
  didAgree = false;
  stoneCount = signal<number | null>(null);

  constructor() {
    effect(async () => {
      if (this.selectedAttributes().gemstoneType) {
        this.loadingService.load('AttributeMenu::DisplayedValue::StoneCounts');
        const stoneCount = await this.stoneCountsProvider.get(this.selectedAttributes());
        this.stoneCount.set(stoneCount);
        this.loadingService.done('AttributeMenu::DisplayedValue::StoneCounts');
      } else {
        this.stoneCount.set(0);
      }
    }, { allowSignalWrites: true });
  }

  attributesToShow = computed(() => {
    const views = this.navigationService.views();
    const attributesToShow = Object.keys(this.selectedAttributes()).reduce((obj, key) => {
      if (key.includes('Material')) {
        if (key.includes('primary')) {
          const primaryMaterialView = views.find(v => v.title === 'Edge Material');
          assert(primaryMaterialView, 'Edge material view was null');
          obj[primaryMaterialView?.displayTitle ?? primaryMaterialView?.title] = this.selectedAttributes().primaryMaterial?.displayName;
        } else {
          obj['Center Material'] = this.selectedAttributes().secondaryMaterial?.displayName;
        }
        return obj;
      }

      if (key === 'gemstoneType') {
        const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
        const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);
        obj['gemstones'] = !this.selectedAttributes().gemstoneType ? null : `${this.stoneCount()} x ${this.selectedAttributeService.configuredItem().gemstoneSize?.displayName} ${typeWithShape?.displayName} ${arrangementWithSetting?.displayName}`;
        return obj;
      }

      if (key === 'engravingType') {
        const { engravingType, engravingText } = this.selectedAttributes();
        obj['Engraving'] = null;
        if (engravingType && engravingText) {
          obj['Engraving'] = `${engravingText}`;
        }
        return obj;
      }
      if (['engravingText'].includes(key)) {
        return obj;
      }
      if (key === 'size') {
        obj['Finger Size'] = this.selectedAttributes().size?.displayName;
        return obj;
      }

      if (key === 'form') {
        obj['Profile'] = this.selectedAttributes().form?.displayName;
        return obj;
      }

      if (key === 'width') {
        obj['Width'] = this.selectedAttributes().width?.displayName;
        return obj;
      }

      const keyArr = key.split('');
      keyArr[0] = keyArr[0].toUpperCase();
      const newKey = keyArr.join('');
      obj[newKey] = this.getSelectedAttributeValueForKey(key);
      return obj;
    }, {} as any)
    return attributesToShow
  });
  // attributeKeys = ['Design', 'Form', 'Edge Material', 'Center Material', 'Width', 'Finger Size'];// = computed(() => Object.keys(this.attributesToShow()));
  attributeKeys = computed(() => this.navigationService.views().filter(v => !v.isHidden && v.title !== 'Review').map(v => v.displayTitle ?? v.title));
  shouldHighlightIncompletes = computed(() => this.attributeMenuService.shouldHilightRequired());

  private getSelectedAttributeValueForKey(key: string) {
    const nameKeys = ['design', 'form'];
    const val = this.selectedAttributes()[key as keyof(ConfiguredItem)];
    if (nameKeys.includes(key)) {
      return (val as any)?.displayName ?? null;
    }
    return val;
  }

  getAttributeValue(attribute: string) {
    return this.attributesToShow()[attribute];
  }

  getStyle(attribute: string) {
    if (attribute === 'Engraving') {
      if (this.selectedAttributes().engravingType?.displayName === 'Block') {
        return {font: '1rem "engraving_block"'}
      }

      if (this.selectedAttributes().engravingType?.displayName === 'Script') {
        return {font: '1.4rem "engraving_script"', 'line-height': '1.2rem'};
      }

      return { color: '#7f7f7f' }
    }

    return null;
  }

  isRequired(attribute: string) {
    return attribute !== 'Engraving';
  }

  isIncomplete(attribute: string) {
    if (attribute === 'Engraving') {
      return false;
    }

    return !this.getAttributeValue(attribute);
  }

  hasConflict(attribute: string) {
    return this.selectedAttributeService.getConflicts().some(c => c.toLowerCase().includes(attribute.toLowerCase()));
  }

  async menuItemClick(attribute: string) {
    this.triggerGAEvent('click', 'menu_item', attribute);

    const viewIndexToNavigateTo = this.navigationService.views().findIndex(v => v.displayTitle === attribute || v.title === attribute);
    this.navigationService.navigateTo(viewIndexToNavigateTo);
  }

  summaryClick() {
    this.triggerGAEvent('click', 'menu_item', 'Review');
    this.navigationService.present({ title: 'Review', type: CheckoutSummaryComponent, doesAppearInProgressBar: true });
  }

    triggerGAEvent(action: string, title: string, value: string) {
    if(this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'menu', title, value);
    }
  }

  removeGemstones() {
    this.selectedAttributeService.removeGemstones();
    const designView = this.navigationService.views().findIndex(v => v.title === 'Design');
    this.navigationService.navigateTo(designView);
  }

  closeButtonClicked() {
    this.navigationService.dismissAll();
  }
}
